/**
 * Read-adapter composing the dashboard's KPI cards (C5, #21936) from data
 * ALREADY resolved by earlier children of this epic — `RealEmployeeRecord[]`
 * (C3, #21934), `RealPayRunRecord[]` (C4a, #21954) — plus two NEW real
 * sources this pass introduces: the entity's statutory filing calendar
 * (`filingCalendar.calendar`) and its entity-wide activity feed
 * (`audit.list`). Pure, no React, no network — mirrors
 * `wagepro_store_pay_runs.ts` / `wagepro_store_employees.ts`'s adapter
 * pattern exactly: the `Real*` interfaces here are the permanent adapter
 * shapes (survive the #21929 employer-admin OAuth scope work unchanged;
 * only HOW they get resolved changes — see `lib/auth/dev_dashboard.ts` for
 * today's dev-bypass-only resolvers), and `deriveDashboardSummary` /
 * `deriveJoinersMissingBankCount` are the permanent seams `wagepro_store.tsx`
 * calls.
 *
 * Per the issue's own acceptance criteria, this is a PURE composition over
 * already-wired procedures — no new backend procedure was added or is
 * needed. `payroll.listEmployees` and `payroll.listPayRuns`/`getPayRun` are
 * reused via the raw `RealEmployeeRecord[]`/`RealPayRunRecord[]` the store
 * already resolves for the employee directory and pay-run history; this
 * module never re-fetches either.
 *
 * ── Field-by-field "mock unchanged when its own input is absent" ──────────
 *
 * Unlike `derivePayRunsFromRecords` (one homogeneous list, one real/mock
 * switch), `DashboardSummary` bundles several independently-resolvable
 * concerns into one object. Each field falls back to `mock`'s own value
 * ONLY when the specific input it depends on is `undefined` — exactly like
 * every other field in this epic, resolved or not, independently:
 *
 * - `headcount` / `headcount_delta` — gated on `employeeRecords`.
 * - `gross_payroll` / `gross_payroll_delta_bp` — gated on `payRunRecords`.
 * - `compliance_alerts` / `statutory_deadline` — gated on `filingPeriods`.
 * - `activity` — gated on `activityRows` (AND, secondarily, `payRunRecords`
 *   for period-joining — see {@link deriveActivity}).
 *
 * `company_name` / `greeting_name` / `expiring_documents` are NEVER
 * touched by this module — they stay exactly as `mock` left them.
 * `company_name`/`greeting_name` are session-identity concerns (C2,
 * #21933's `wagepro_store_session_facts.ts` scope, not this card map).
 * `expiring_documents` has no real source at all — see module docblock
 * below on the deferred cards. `period` USED to also be untouched (the
 * dashboard showed the fixture's April 2026 to every tenant, issue #24350)
 * -- it is now derived from the tenant's own pay runs (open cycle first,
 * else the latest approved cycle, else the reference date), so the
 * subtitle never renders the fixture's month.
 *
 * ── The three deferred cards (issue's own explicit list) ───────────────────
 *
 * - Expiring-documents: no employee/company document-expiry concept exists
 *   anywhere in the schema. `MOCK_DASHBOARD.expiring_documents` was retired
 *   to `[]` (see `mock_data.ts`) rather than kept as a permanently-fake
 *   demo array with no backing feature — `ExpiringDocumentsCard` renders an
 *   `EmptyState` for the empty case. This module does not touch the field.
 * - Update-requests count: depends on a change-request backend that does
 *   not exist. `use_dashboard.ts` no longer emits an `update_requests_open`
 *   action item at all (dropped, not wired to a permanent literal `0` row).
 * - Pending-expenses: depends on the undecided `use_expenses` scope
 *   question (epic non-goal) — `use_dashboard.ts`'s pre-existing
 *   `expenses_pending` action item (mock-store-derived) is untouched by
 *   this pass; not this module's concern.
 *
 * ── Activity feed: why only two of four mock kinds ever appear ─────────────
 *
 * Same reasoning as `wagepro_store_pay_run_audit.ts` (C4c, #21956), applied
 * entity-wide instead of per-document: only `payroll.pay_run.create` ->
 * `pay_run_created` and `payroll.pay_run.approve` -> `pay_run_approved` have
 * a real, reachable `audit_log` source. `exceptions_acknowledged` (never
 * persisted server-side) and `expense_submitted` (routed through a
 * different, out-of-this-epic's-scope backend) never appear — dropped
 * silently, not a bug. See `dev_dashboard.ts`'s docblock for why `audit.list`
 * (entity-wide), not `audit.listByDocument` (per-document), is the correct
 * call for this card.
 *
 * ── Statutory deadline: why only epf/etf/apit obligations are considered ──
 *
 * `filingCalendar.calendar` returns periods for ALL SL statutory
 * obligations (vat, sscl, wht, apit, epf, etf),
 * but a pay run's `StatutorySummary` (`deriveStatutorySummary`,
 * `wagepro_store_pay_runs.ts`) only ever computes EPF/ETF/APIT figures —
 * there is no persisted VAT/SSCL/WHT amount anywhere a payroll run
 * touches. Narrowing candidate periods to `{'epf','etf','apit'}` keeps
 * `amount_due` an honest, directly-computable figure instead of a
 * fabricated one for an obligation this module has no data for.
 *
 * `owner` / `filed_via` have no real per-period assignee/channel concept in
 * the schema — deliberately `'—'` for a real deadline (never a fabricated
 * name or channel). (`deriveApproval` in `wagepro_store_pay_runs.ts` used
 * to take the same stance for names; it now returns `''` for "unknown" and
 * the UI hides the name instead.) `amount_due` takes
 * the same stance for a DIFFERENT reason: `null` (rendered `'—'` by
 * `StatutoryDeadlineCard`) whenever `payRunRecords` itself is `undefined`
 * (the pay-run fetch hasn't resolved/failed) — a real due date must never
 * be shown paired with a fabricated "LKR 0.00" owed amount just because a
 * DIFFERENT, independent fetch (pay runs, not the filing calendar) didn't
 * come back (adversarial review finding, C4 #22328). A real, resolved,
 * empty `payRunRecords` array is a distinct, honest `0`.
 *
 * When no unfiled epf/etf/apit period exists in the resolved calendar
 * window, `statutory_deadline` is `null` — a real, distinct "nothing due"
 * state, never a stale mock deadline shown alongside otherwise-real KPIs.
 * `StatutoryDeadlineCard` renders an `EmptyState` for `null`.
 */
import type {
  ActivityEvent,
  BasisPoints,
  DashboardSummary,
  MinorUnits,
  PayPeriod,
  StatutoryDeadline,
  StatutorySummary,
} from './types';
import {
  isParseableIsoDate,
  type RealEmployeeRecord,
} from './wagepro_store_employees';
import {
  deriveStatutorySummary,
  derivePayRunBlockedContractorCount,
  deriveValidPeriodDate,
  derivePayPeriod,
  type RealPayRunRecord,
} from './wagepro_store_pay_runs';

/**
 * One `filingCalendar.calendar` period, narrowed to exactly the fields this
 * adapter reads (see `FilingPeriodWithStatus` in
 * `packages/jumpstone-core/src/jurisdictions/lk/filing_calendar.ts`) — an
 * intentional subset, not a mirror, same stance as `RealPayRunLine`. Plain
 * `string` fields (not the `@ledger/core` `FilingObligation`/
 * `FilingPeriodStatus` literal unions) so this `data/` module never takes a
 * dependency on `@ledger/core` — `dev_dashboard.ts` is the one place that
 * calls the real, strongly-typed procedure.
 */
export interface RealFilingPeriod {
  obligation: string;
  /** Inclusive first day of the filing period (ISO `YYYY-MM-DD`). */
  periodStart: string;
  /** Statutory due date for filing/remitting this period (ISO `YYYY-MM-DD`). */
  dueDate: string;
  status: string;
}

/**
 * One `audit.list` row, narrowed to exactly the fields this adapter reads.
 * Broader than `RealAuditEntryRow` (`wagepro_store_pay_run_audit.ts`, sourced
 * from the per-document `audit.listByDocument`) because `audit.list` is
 * entity-wide and returns every `entity_type`, not just `'pay_run'` rows
 * pre-filtered server-side — `entity_type`/`entity_ref_id` let this adapter
 * do that filtering itself. Same PII-safety stance as `RealAuditEntryRow`:
 * never reads `changes` (arbitrary caller-supplied JSON) or `client_id` /
 * `auth_strength` (non-repudiation attribution, out of scope for this
 * lightweight feed) — see `apps/api/src/trpc/routers/audit.ts`'s
 * `auditEntryProjection` docblock.
 */
export interface RealActivityAuditRow {
  /** `audit_log.id` is an auto-incrementing SQLite `INTEGER` primary key — converted to a string to match `ActivityEvent.id`. */
  id: number;
  /** Free-text wire action string, e.g. `'payroll.pay_run.create'` — narrowed by {@link ACTIVITY_ACTION_TO_KIND}. */
  action: string;
  /** `audit_log.entity_type` — e.g. `'pay_run'`, `'invoice'`, `'bill'`. Only `'pay_run'` rows are ever mapped to an `ActivityEvent` in this pass. */
  entity_type: string;
  /** `audit_log.entity_ref_id` — the id of the row acted upon (a pay-run id, for `entity_type: 'pay_run'` rows). `NOT NULL` on the wire. */
  entity_ref_id: string;
  actor_id: string | null;
  /** Server-resolved display name, or `null` for a system actor — see `audit.ts`'s `list`. */
  actor_display_name: string | null;
  created_at: string;
}

// ── Headcount / joiners missing bank details ────────────────────────────────

export interface HeadcountFields {
  headcount: number;
  headcount_delta: number;
}

/**
 * `headcount` is a live count of active employees. `headcount_delta` has no
 * real source — there is no historical headcount snapshot to diff against
 * (no "headcount as of last cycle" concept anywhere in the schema) — so it
 * is honestly `0` once real employee records resolve, rather than an
 * invented trend. `mock.headcount_delta` (the demo's illustrative "+2 this
 * month") is preserved only when `employeeRecords` itself never resolved.
 */
export function deriveHeadcountFields(
  mock: HeadcountFields,
  employeeRecords: readonly RealEmployeeRecord[] | undefined,
): HeadcountFields {
  if (employeeRecords === undefined) return mock;
  const headcount = employeeRecords.filter(
    (employee) => employee.is_active,
  ).length;
  return { headcount, headcount_delta: 0 };
}

/**
 * An active employee, who has actually started (a non-null
 * `employment_start_date`, ruling out a future-dated pending hire), with no
 * bank account number recorded — exactly the three fields the issue's own
 * card->source map names (`is_active`, null `bank_account_number`,
 * `employment_start_date`). Deliberately does NOT also check `bank_name`
 * (unlike `wagepro_store_pay_runs.ts`'s `isBankDetailsIncomplete`, used for
 * a per-run wizard exception) — this dashboard tile is scoped to exactly the
 * fields the issue names, nothing broader.
 */
function isJoinerMissingBank(employee: RealEmployeeRecord): boolean {
  return (
    employee.is_active &&
    employee.employment_start_date !== null &&
    (employee.bank_account_number === null ||
      employee.bank_account_number === '')
  );
}

/**
 * Count for the `joiners_missing_bank_details` action item. `mock` is the
 * hook's existing hardcoded illustrative count
 * (`MOCK_JOINERS_MISSING_BANK_COUNT`, `mock_data.ts`), preserved only when
 * `employeeRecords` never resolved.
 */
export function deriveJoinersMissingBankCount(
  mock: number,
  employeeRecords: readonly RealEmployeeRecord[] | undefined,
): number {
  if (employeeRecords === undefined) return mock;
  return employeeRecords.filter(isJoinerMissingBank).length;
}

// ── Gross payroll + delta ────────────────────────────────────────────────

export interface GrossPayrollFields {
  gross_payroll: MinorUnits;
  /** `null` when there is no honest previous cycle to compare against — see `deriveGrossPayrollFields` docblock. */
  gross_payroll_delta_bp: BasisPoints | null;
}

/** Most-recently-created run first. `created_at` (an actual write-time timestamp) is used rather than `period_end` so "latest"/"prior" reflects real creation order even if periods were ever created out of calendar sequence (e.g. a backfilled prior-period run). */
function sortByCreatedAtDesc(
  records: readonly RealPayRunRecord[],
): RealPayRunRecord[] {
  return [...records].sort((a, b) => {
    if (a.created_at === b.created_at) return 0;
    return a.created_at > b.created_at ? -1 : 1;
  });
}

/** 1% = 100 basis points (`docs/money-rules.md`), so a fractional ratio -> bp conversion multiplies by 10,000. */
const BASIS_POINTS_PER_UNIT = 10_000;

/**
 * Voided pay runs are excluded from every "current cycle" derivation on the
 * dashboard — a voided run has its GL reversed and represents a run that no
 * longer counts against gross/period. Including it would let a voided
 * pay-run drive the subtitle's period and the gross-payroll tile
 * (adversarial review finding, #24350 fixup): a tenant whose latest-created
 * run was voided would see that voided cycle rendered as the "current" one.
 */
function isLiveRun(run: RealPayRunRecord): boolean {
  return run.status !== 'voided';
}

/**
 * `gross_payroll` is the latest live pay run's raw `total_gross_cents` (the
 * wire field on the `pay_runs` row itself, not the draft-vs-approved
 * journal-preview override `wagepro_store_pay_runs.ts`'s UI adapter applies
 * — the issue asks for a literal `total_gross_cents` comparison).
 * `gross_payroll_delta_bp` compares it against the live run created
 * immediately before it. No prior live run (fewer than two exist) or a
 * zero-gross prior run (division by zero) both honestly resolve to `null` —
 * there is no meaningful percentage change to report, and rendering "0% vs
 * last cycle" in that state is misleading (issue #24350 — reads as
 * "unchanged" rather than "no comparison possible"). The card renders "No
 * previous cycle" for `null`.
 *
 * A pay-run history containing only voided runs (or zero resolved runs)
 * resolves `{ gross_payroll: 0, gross_payroll_delta_bp: null }` — a real
 * state distinct from "not resolved".
 */
export function deriveGrossPayrollFields(
  mock: GrossPayrollFields,
  payRunRecords: readonly RealPayRunRecord[] | undefined,
): GrossPayrollFields {
  if (payRunRecords === undefined) return mock;
  const sorted = sortByCreatedAtDesc(payRunRecords.filter(isLiveRun));
  const latest = sorted.at(0);
  if (!latest) {
    return { gross_payroll: 0, gross_payroll_delta_bp: null };
  }
  const prior = sorted.at(1);
  if (!prior || prior.total_gross_cents === 0) {
    return {
      gross_payroll: latest.total_gross_cents,
      gross_payroll_delta_bp: null,
    };
  }
  const ratio =
    (latest.total_gross_cents - prior.total_gross_cents) /
    prior.total_gross_cents;
  return {
    gross_payroll: latest.total_gross_cents,
    gross_payroll_delta_bp: Math.round(ratio * BASIS_POINTS_PER_UNIT),
  };
}

/** The latest (most-recently-created) LIVE pay run, or `undefined` for an empty/absent list. Voided runs are excluded — same reasoning as `deriveGrossPayrollFields`; the statutory-deadline card and the gross-payroll tile must agree on which run is "the latest". */
function pickLatestPayRun(
  payRunRecords: readonly RealPayRunRecord[] | undefined,
): RealPayRunRecord | undefined {
  if (payRunRecords === undefined || payRunRecords.length === 0) {
    return undefined;
  }
  return sortByCreatedAtDesc(payRunRecords.filter(isLiveRun)).at(0);
}

/**
 * The pay run whose OWN period (year+month, same derivation as
 * `derivePayRunsFromRecords`) matches `period`, or `undefined` if none does.
 *
 * `deriveStatutoryDeadline` prefers this over `pickLatestPayRun` — mid-cycle,
 * the latest-CREATED run is often the NEXT period's still-open draft, not the
 * period the picked filing deadline actually belongs to (e.g. "July's EPF,
 * due Aug 15" priced off August's draft would be wrong — adversarial review
 * finding, C5, #21936). Falls back to the overall latest run only when no
 * run for the exact period exists (e.g. that period's run was never created,
 * voided, or the entity keeps no history for it) — a same-cycle approximate
 * figure is still more useful than an honest `0`.
 *
 * Voided runs are filtered out BEFORE the period match — the docblock of
 * `deriveStatutoryDeadline`'s "voided falls through" contract already
 * assumes this; without the filter, a voided run for the deadline's period
 * would still price the tile (adversarial review finding, #24350 fixup).
 * Consistent with {@link pickLatestPayRun} and every other "current
 * cycle" derivation in this module.
 */
function pickPayRunForPeriod(
  payRunRecords: readonly RealPayRunRecord[] | undefined,
  period: PayPeriod,
): RealPayRunRecord | undefined {
  if (payRunRecords === undefined) return undefined;
  return payRunRecords.filter(isLiveRun).find((run) => {
    const runPeriod = derivePayPeriod(
      deriveValidPeriodDate(run.period_start, run.created_at),
    );
    return runPeriod.year === period.year && runPeriod.month === period.month;
  });
}

// ── Compliance alerts + statutory deadline ──────────────────────────────

/** The only obligations a pay run's `StatutorySummary` can honestly price — see module docblock. Shared by {@link deriveComplianceCounts} and {@link selectStatutoryDeadline} so the tile and the deadline card agree on which obligations belong to WagePro's own scope. */
export const PAYROLL_STATUTORY_OBLIGATIONS: ReadonlySet<string> = new Set([
  'epf',
  'etf',
  'apit',
]);

/** Split of `filingPeriods` by urgency — `overdue` and `due_soon` separately, plus the sum. Powers both the compliance-alerts KPI tile and its "N overdue · M due soon" caption. */
export interface ComplianceCounts {
  overdue: number;
  due_soon: number;
  total: number;
}

/**
 * Splits the filing calendar's payroll-obligation periods (epf/etf/apit only —
 * VAT/SSCL/WHT/CIT belong to accounting surfaces WagePro can't act on) into
 * overdue vs. due-soon so the KPI tile can render an honest caption
 * ("3 overdue · 5 due soon" rather than the previous, undifferentiated
 * "Filing due soon" — issue #24350's finding 6).
 */
export function deriveComplianceCounts(
  filingPeriods: readonly RealFilingPeriod[],
): ComplianceCounts {
  let overdue = 0;
  let dueSoon = 0;
  for (const period of filingPeriods) {
    if (!PAYROLL_STATUTORY_OBLIGATIONS.has(period.obligation)) continue;
    if (period.status === 'overdue') overdue += 1;
    else if (period.status === 'due_soon') dueSoon += 1;
  }
  return { overdue, due_soon: dueSoon, total: overdue + dueSoon };
}

/**
 * Kept for backwards compatibility with the tile-status/mock-fallback
 * plumbing (`resolveDashboardState`'s `deriveComplianceAlertsCount` path).
 * The new caption uses {@link deriveComplianceCounts} for its overdue vs.
 * due-soon split.
 */
export function deriveComplianceAlertsCount(
  mock: number,
  filingPeriods: readonly RealFilingPeriod[] | undefined,
): number {
  if (filingPeriods === undefined) return mock;
  return deriveComplianceCounts(filingPeriods).total;
}

/**
 * Discriminant for the compliance-alerts KPI tile's caption — the caller
 * maps `kind` to a literal `t('wp.dashboard.stat.compliance_caption_<kind>',
 * …)` call. Returning a raw i18n key here would defeat the static extractor
 * (`packages/config-eslint/no-dynamic-i18n-keys.js`), so this shape mirrors
 * the sanctioned migration pattern documented in that rule.
 */
export interface ComplianceCaptionModel {
  kind: 'both' | 'overdue' | 'due_soon' | 'none';
  overdue: number;
  due_soon: number;
}

/**
 * Picks the compliance-alerts tile caption discriminant. Both sides non-zero
 * → `both` (the full "N overdue · M due soon"); one side zero → only the
 * non-zero half (never a "0 overdue" that reads as either "nothing wrong" or
 * noise); both zero → `none` (the "nothing due" caption). Pure so the KPI
 * tile does a single `switch` on `kind` and never sees a dynamic key.
 */
export function deriveComplianceCaption(
  overdue: number,
  dueSoon: number,
): ComplianceCaptionModel {
  if (overdue > 0 && dueSoon > 0) {
    return { kind: 'both', overdue, due_soon: dueSoon };
  }
  if (overdue > 0) return { kind: 'overdue', overdue, due_soon: 0 };
  if (dueSoon > 0) return { kind: 'due_soon', overdue: 0, due_soon: dueSoon };
  return { kind: 'none', overdue: 0, due_soon: 0 };
}

/** Discriminant for the greeting shown in the dashboard header — the caller maps this to a literal `t('wp.dashboard.greeting_<part>', …)` call. Returning a raw i18n key would defeat the static extractor; see {@link deriveComplianceCaption}'s docblock for the same reasoning. */
export type GreetingPart = 'morning' | 'afternoon' | 'evening';

/**
 * Time-of-day greeting discriminant: 00..11 morning, 12..16 afternoon,
 * 17..23 evening. `hour` must already be the caller's local hour (0..23) —
 * this helper does NOT read the wall clock so it is fully deterministic.
 * `dashboard_screen.tsx` calls `new Date().getHours()` at render time and
 * passes the result in.
 */
export function greetingPartForHour(hour: number): GreetingPart {
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

/**
 * Selects which unfiled payroll obligation to show and whether to present it
 * as overdue or upcoming. Pure — never reads the wall clock — so it is
 * unit-testable with an arbitrary pinned `referenceDate`.
 *
 *  - `overdue`: at least one unfiled epf/etf/apit period is past its due
 *    date; pick the MOST overdue (biggest positive `days`) so the operator
 *    sees the worst offender first. Expose `next` for the nearest upcoming
 *    unfiled one so the card can render "Next: <period> due <date>" as a
 *    second line — the "what's ahead once I clear this" answer.
 *  - `upcoming`: nothing overdue; pick the nearest future unfiled period.
 *    `next` is `undefined`.
 *  - `none`: no unfiled epf/etf/apit period exists at all.
 *
 * `days` is ALWAYS a non-negative integer — the card renders "Overdue by N
 * days" or "Due in N days" and must never show a signed negative countdown
 * (issue #24350's "Due in -31 days" regression). Whether N is
 * days-past-due or days-remaining is disambiguated by `kind`.
 */
export function selectStatutoryDeadline(
  filingPeriods: readonly RealFilingPeriod[],
  referenceDate: string,
):
  | { kind: 'none' }
  | {
      kind: 'overdue';
      days: number;
      picked: RealFilingPeriod;
      next: RealFilingPeriod | undefined;
    }
  | {
      kind: 'upcoming';
      days: number;
      picked: RealFilingPeriod;
      next: undefined;
    } {
  const candidates = filingPeriods.filter(
    (period) =>
      PAYROLL_STATUTORY_OBLIGATIONS.has(period.obligation) &&
      period.status !== 'filed' &&
      isParseableIsoDate(period.dueDate) &&
      isParseableIsoDate(period.periodStart),
  );
  if (candidates.length === 0) return { kind: 'none' };

  const referenceIsValid = isParseableIsoDate(referenceDate);
  const overdue: RealFilingPeriod[] = [];
  const upcoming: RealFilingPeriod[] = [];
  for (const period of candidates) {
    // No parseable reference date -> classify everything as upcoming, so a
    // rendered card still shows a positive day count of 0 rather than a
    // negative one. Same "never NaN, never negative" guarantee as before.
    if (referenceIsValid && daysBetweenIso(referenceDate, period.dueDate) < 0) {
      overdue.push(period);
    } else {
      upcoming.push(period);
    }
  }

  if (overdue.length > 0) {
    // Most overdue first (earliest due date wins).
    const sortedOverdue = [...overdue].sort((a, b) =>
      a.dueDate === b.dueDate ? 0 : a.dueDate < b.dueDate ? -1 : 1,
    );
    const picked = sortedOverdue[0];
    const nextUpcoming =
      upcoming.length > 0
        ? [...upcoming].sort((a, b) =>
            a.dueDate === b.dueDate ? 0 : a.dueDate < b.dueDate ? -1 : 1,
          )[0]
        : undefined;
    const days = referenceIsValid
      ? Math.abs(daysBetweenIso(referenceDate, picked.dueDate))
      : 0;
    return { kind: 'overdue', days, picked, next: nextUpcoming };
  }

  // Nearest upcoming first.
  const sortedUpcoming = [...upcoming].sort((a, b) =>
    a.dueDate === b.dueDate ? 0 : a.dueDate < b.dueDate ? -1 : 1,
  );
  const picked = sortedUpcoming[0];
  const days = referenceIsValid
    ? daysBetweenIso(referenceDate, picked.dueDate)
    : 0;
  return { kind: 'upcoming', days, picked, next: undefined };
}

/** Whole calendar days from `fromIso` to `toIso` (both `YYYY-MM-DD`, UTC midnight) — negative when `toIso` is in the past, matching `StatutoryDeadlineCard`'s `days_remaining <= 7`/`<= 0`-style urgency checks. */
function daysBetweenIso(fromIso: string, toIso: string): number {
  const fromMs = Date.parse(`${fromIso}T00:00:00Z`);
  const toMs = Date.parse(`${toIso}T00:00:00Z`);
  return Math.round((toMs - fromMs) / 86_400_000);
}

function amountDueForObligation(
  obligation: string,
  statutory: StatutorySummary,
): MinorUnits {
  switch (obligation) {
    case 'epf':
      return statutory.epf_employee + statutory.epf_employer;
    case 'etf':
      return statutory.etf_employer;
    case 'apit':
      return statutory.apit_withheld;
    default:
      return 0;
  }
}

/**
 * `referenceDate` (`YYYY-MM-DD`) is the SAME "today" the caller used to
 * build the `filingCalendar.calendar` request's `range_start`/`range_end`/
 * `reference_date` (see `dev_dashboard.ts`) — never re-read from the wall
 * clock inside this pure function, so it is fully deterministic and
 * testable with an arbitrary pinned date.
 *
 * Overdue vs. upcoming selection and the never-negative `days_remaining`
 * contract are delegated to {@link selectStatutoryDeadline} (issue #24350).
 */
export function deriveStatutoryDeadline(
  mock: StatutoryDeadline | null,
  filingPeriods: readonly RealFilingPeriod[] | undefined,
  payRunRecords: readonly RealPayRunRecord[] | undefined,
  referenceDate: string,
): StatutoryDeadline | null {
  if (filingPeriods === undefined) return mock;

  const selection = selectStatutoryDeadline(filingPeriods, referenceDate);
  if (selection.kind === 'none') return null;

  const picked = selection.picked;
  const periodDate = deriveValidPeriodDate(
    picked.periodStart,
    picked.periodStart,
  );
  const period = derivePayPeriod(periodDate);
  // Prefer the run for the SAME period this deadline is for; only fall back
  // to "whatever's most recent" when no run for that exact period exists —
  // see `pickPayRunForPeriod`'s docblock for why this matters mid-cycle.
  const sourceRun =
    pickPayRunForPeriod(payRunRecords, period) ??
    pickLatestPayRun(payRunRecords);
  // `payRunRecords === undefined` means the pay-run fetch genuinely hasn't
  // resolved/failed -- `amount_due` must be an honest "unknown" (`null`),
  // never a fabricated `0` shown alongside a real due date (adversarial
  // review finding, C4 #22328: `filingPeriods` resolving independently of
  // `payRunRecords` used to silently produce a real deadline paired with a
  // fake "LKR 0.00" owed amount). A real, RESOLVED, empty `payRunRecords`
  // array (a genuinely new entity with no pay-run history yet) still
  // honestly resolves `0` -- that is a real state, not a failure artifact.
  //
  // A draft blocked by a worker switched to 'contractor' (WagePro worker
  // types) has placeholder-zero preview totals, not real figures — its
  // amount due is honestly unknown (`null`), never a fabricated `0`.
  const amountDue =
    payRunRecords === undefined
      ? null
      : sourceRun && derivePayRunBlockedContractorCount(sourceRun) > 0
        ? null
        : sourceRun
          ? amountDueForObligation(
              picked.obligation,
              deriveStatutorySummary(sourceRun),
            )
          : 0;

  const next =
    selection.kind === 'overdue' && selection.next
      ? {
          period: derivePayPeriod(
            deriveValidPeriodDate(
              selection.next.periodStart,
              selection.next.periodStart,
            ),
          ),
          due_on: selection.next.dueDate,
        }
      : null;

  return {
    kind: selection.kind,
    period,
    amount_due: amountDue,
    // No real per-period filing-channel/assignee concept — see module docblock.
    filed_via: '—',
    owner: '—',
    due_on: picked.dueDate,
    days_remaining: selection.days,
    next,
  };
}

// ── Activity feed ─────────────────────────────────────────────────────────

/** The two real `audit_log.action` values this adapter recognises — see module docblock for why the other two mock `ActivityEvent` kinds never appear. `Partial<Record<...>>` (not a bare `Record<string, ...>`) so an unrecognised `action` string correctly types as `undefined`, not a false-positive member of the closed kind union. */
const ACTIVITY_ACTION_TO_KIND: Readonly<
  Partial<Record<string, 'pay_run_created' | 'pay_run_approved'>>
> = {
  'payroll.pay_run.create': 'pay_run_created',
  'payroll.pay_run.approve': 'pay_run_approved',
};

/** The pay-run `entity_type` audit rows are logged against — matches `entity_type: 'pay_run'` in every `logAuditEntry` call in `apps/api/src/trpc/routers/payroll.ts`. */
const PAY_RUN_ENTITY_TYPE = 'pay_run';

function toActivityEvent(
  row: RealActivityAuditRow,
  payRunsById: ReadonlyMap<string, RealPayRunRecord>,
): ActivityEvent | null {
  if (row.entity_type !== PAY_RUN_ENTITY_TYPE) return null;
  const kind = ACTIVITY_ACTION_TO_KIND[row.action];
  if (kind === undefined) return null;

  const payRun = payRunsById.get(row.entity_ref_id);
  // No matching pay-run record to source a `period` from (e.g. `payRuns`
  // itself failed to resolve independently of `activityRows`, or the run
  // was later deleted) -- drop the entry rather than fabricating a period.
  if (!payRun) return null;

  const period = derivePayPeriod(
    deriveValidPeriodDate(payRun.period_start, payRun.created_at),
  );
  const actor = row.actor_display_name ?? row.actor_id ?? 'System';

  return kind === 'pay_run_created'
    ? { id: String(row.id), kind, at: row.created_at, period }
    : { id: String(row.id), kind, at: row.created_at, actor, period };
}

/**
 * Derives the dashboard's activity feed, or passes `mock` through UNCHANGED
 * when `activityRows` is `undefined` (the resolver failed, or hasn't run)
 * — same "real undefined -> mock unchanged" contract as every other adapter
 * in this epic.
 *
 * An explicitly EMPTY `activityRows` (a real entity with zero recognised
 * events yet) is a real, distinct state from "not resolved" — it renders an
 * empty feed, it does NOT fall back to `mock`.
 *
 * When `payRunRecords` itself is `undefined` (the audit resolver succeeded
 * independently of the pay-runs one — possible, since they are two
 * unrelated fan-out calls, see `dev_dashboard.ts`), no event can be
 * period-joined, so this resolves `[]` rather than a period-less/fabricated
 * entry for every row.
 */
export function deriveActivity(
  mock: ActivityEvent[],
  activityRows: readonly RealActivityAuditRow[] | undefined,
  payRunRecords: readonly RealPayRunRecord[] | undefined,
): ActivityEvent[] {
  if (activityRows === undefined) return mock;
  if (payRunRecords === undefined) return [];

  const payRunsById = new Map(payRunRecords.map((run) => [run.id, run]));
  const events: ActivityEvent[] = [];
  for (const row of activityRows) {
    const event = toActivityEvent(row, payRunsById);
    if (event !== null) events.push(event);
  }
  return events;
}

// ── Period ────────────────────────────────────────────────────────────────

/**
 * The pay-run status that represents an "open" cycle currently being worked
 * — a `draft` run. The real backend's `pay_runs.status` union is
 * `'draft' | 'approved' | 'paid' | 'voided'` (verified against
 * `apps/api/src/db/schema/tenant/payroll.ts`; the `PayRunStatus`
 * `'in_review'` / `'filed'` members are mock-only and never emitted by the
 * real adapter — see `wagepro_store_pay_runs.ts`'s `RealPayRunStatus`
 * docblock). `voided` is excluded (see {@link isLiveRun}).
 */
const OPEN_CYCLE_STATUSES = new Set<string>(['draft']);

/**
 * The dashboard subtitle's "Payroll period {period}" is the CURRENT cycle
 * the operator is working, NOT the fixture (issue #24350: every tenant used
 * to see "April 2026" because `deriveDashboardSummary` never set `period`
 * and `MOCK_DASHBOARD.period` was `{ 2026, 4 }`). Selection:
 *
 *  1. A draft run — the cycle being built right now.
 *  2. Else the most recently created LIVE approved+ run — the last cycle
 *     actually closed. Voided runs are excluded — see {@link isLiveRun}.
 *  3. Else the reference-date's own month — a genuinely new entity with no
 *     pay history at all, or one whose only history is voided. Never falls
 *     back to the fixture.
 */
export function derivePeriodFromRuns(
  payRunRecords: readonly RealPayRunRecord[] | undefined,
  referenceDate: string,
): PayPeriod {
  const runs = payRunRecords ?? [];
  const openCycle = sortByCreatedAtDesc(
    runs.filter((run) => OPEN_CYCLE_STATUSES.has(run.status)),
  ).at(0);
  const chosen = openCycle ?? pickLatestPayRun(runs);
  if (chosen) {
    return derivePayPeriod(
      deriveValidPeriodDate(chosen.period_start, chosen.created_at),
    );
  }
  // No runs at all -- use the reference date. `deriveValidPeriodDate`
  // guarantees a parseable `YYYY-MM-DD` result; feed it a safe fallback
  // date-string when the caller passes an unparseable reference date so
  // this never throws.
  const safeReference = isParseableIsoDate(referenceDate)
    ? referenceDate
    : '1970-01-01';
  return derivePayPeriod(safeReference);
}

// ── Pending queues ────────────────────────────────────────────────────────

/**
 * Number of leave requests still awaiting a decision — the tenant-side signal
 * the dashboard's pending-approvals tile counts. Pure so this is unit-testable
 * without any React or fetch mocking; separated out from the fetch layer so
 * a leave-requests hook, the dashboard, and any future queue widget all read
 * the same "pending" definition (a `status === 'pending'` row).
 */
export function derivePendingLeaveCount(
  requests: readonly { status: string }[],
): number {
  let count = 0;
  for (const request of requests) {
    if (request.status === 'pending') count += 1;
  }
  return count;
}

/**
 * Number of expense claims still awaiting a decision. Same rationale as
 * {@link derivePendingLeaveCount}. Pending on the wire is `submitted` — the
 * pre-approval state on the real `expense_claims.status` union
 * (`apps/api/src/db/schema/tenant/expense_claims.ts`). The UI-side
 * `Expense.status = 'pending'` is a re-labelling
 * (`wagepro_store_expenses.ts`'s `QUEUEABLE_STATUS_MAP`); this helper is
 * intentionally callable against BOTH — the wire `submitted` rows and any
 * UI-rebadged `pending` list — because the dashboard fetches raw claim
 * records for the count only.
 */
export function derivePendingExpenseCount(
  claims: readonly { status: string }[],
): number {
  let count = 0;
  for (const claim of claims) {
    if (claim.status === 'submitted' || claim.status === 'pending') count += 1;
  }
  return count;
}

// ── Composition ──────────────────────────────────────────────────────────

/** Every real input `deriveDashboardSummary` composes over — see module docblock for each field's own independent "mock unchanged when absent" contract. */
export interface DashboardRealInputs {
  employeeRecords: readonly RealEmployeeRecord[] | undefined;
  payRunRecords: readonly RealPayRunRecord[] | undefined;
  filingPeriods: readonly RealFilingPeriod[] | undefined;
  activityRows: readonly RealActivityAuditRow[] | undefined;
  /** "Today", `YYYY-MM-DD` — see {@link deriveStatutoryDeadline}'s docblock on why this is threaded in rather than read from the wall clock here. */
  referenceDate: string;
}

/**
 * Composes the dashboard's `DashboardSummary` from `mock` plus whichever
 * real inputs have resolved. `company_name` / `greeting_name` /
 * `expiring_documents` still pass through from `mock` untouched — see
 * module docblock. `period` is now DERIVED from the tenant's pay-run
 * history (issue #24350) instead of the fixture's April 2026.
 */
export function deriveDashboardSummary(
  mock: DashboardSummary,
  inputs: DashboardRealInputs,
): DashboardSummary {
  const headcountFields = deriveHeadcountFields(
    { headcount: mock.headcount, headcount_delta: mock.headcount_delta },
    inputs.employeeRecords,
  );
  const grossPayrollFields = deriveGrossPayrollFields(
    {
      gross_payroll: mock.gross_payroll,
      gross_payroll_delta_bp: mock.gross_payroll_delta_bp,
    },
    inputs.payRunRecords,
  );

  return {
    ...mock,
    ...headcountFields,
    ...grossPayrollFields,
    period: derivePeriodFromRuns(inputs.payRunRecords, inputs.referenceDate),
    compliance_alerts: deriveComplianceAlertsCount(
      mock.compliance_alerts,
      inputs.filingPeriods,
    ),
    statutory_deadline: deriveStatutoryDeadline(
      mock.statutory_deadline,
      inputs.filingPeriods,
      inputs.payRunRecords,
      inputs.referenceDate,
    ),
    activity: deriveActivity(
      mock.activity,
      inputs.activityRows,
      inputs.payRunRecords,
    ),
  };
}
